import * as React from "react";
import { connect } from "react-redux";
import { Card, Grid, Tab, TabProps } from "semantic-ui-react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { Link } from "@teamprevent/common/task/taskActions/models";
import { CurrentUser } from "@teamprevent/common/model";

import { Dispatch, useDispatch, useSelector } from "@teamprevent/customer-redux";

import { OldCaseConversationTab } from "@teamprevent/common/messaging/components/OldCaseConversationTab";

import { OhCaseDetailModel } from "../model";
import { ohCaseSelector, basePathSelector, routeIdSelector } from "../selectors";

import { ApplicationState } from "../../../applicationState";
import { MessagingAuthorizer } from "../../../messaging/messagingAuthorizer";

import { loadOhCaseAppointments, loadOhCaseDetail, loadOhCaseDocuments } from "../actions";

import * as details from "./details";

import { push } from "redux-little-router";

export interface CoreDetailProps {
    ohCase: OhCaseDetailModel;
    currentCustomer: Link;
    currentUser: CurrentUser;
}

export interface DetailProps extends CoreDetailProps {
    path: string;
    basePath: string;
    tab?: string;
}

export interface DispatchProps {
    push: (path: string) => void;
    loadDocuments: (ohCaseId: string) => void;
}

const tabs = (Object.keys(details) as (keyof typeof details)[]).map(k => details[k]);

export const DetailUnconnected: React.FunctionComponent<DetailProps & DispatchProps> = (props: DetailProps & DispatchProps) => {
    const enableCaseConversationImprovements = getFeatureToggles()[FeatureToggleNames.CaseConversationImprovements];
    const [documentsLoaded, setDocumentsLoaded] = React.useState(false);

    const dispatch = useDispatch();
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params?.ohCaseId);

    React.useEffect(() => {
        dispatch(loadOhCaseDetail({ ohCaseId }));
        dispatch(loadOhCaseAppointments({ ohCaseId }));
    }, [dispatch, ohCaseId]);

    React.useEffect(() => {
        if (props.ohCase?.id && !documentsLoaded) {
            props.loadDocuments(props.ohCase.id);
            setDocumentsLoaded(true);
        }
    }, [documentsLoaded, props]);

    const handleTabChange = React.useCallback((_, { activeIndex }: TabProps) => {
        props.push(`${props.basePath}/${props.ohCase.id}${tabs[activeIndex as unknown as number](undefined).path}`);
    }, [props]);

    const panes: any[] = [details.coreDetailsTab(props), details.documentsTab() ];

    if (MessagingAuthorizer.isAuthorized(props.currentUser)) {
        const conversationTab = enableCaseConversationImprovements ? details.caseConversationTab(props.ohCase.id) : OldCaseConversationTab();

        panes.push(conversationTab);
    }

    const activeIndex = panes.slice(1).findIndex(p => props.tab && props.tab.startsWith(p.path)) + 1;

    const content = () => panes.length === 1
        ? <Card className="not-tabular" fluid>{panes[0].render()}</Card>
        : <Tab className="case-tabular-menu" panes={panes} onTabChange={handleTabChange} activeIndex={activeIndex} />;

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column>
                    <h1>{props.ohCase.employeeFirstName}</h1>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    {content()}
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

const currentTab = (state: ApplicationState) => state.router.pathname.substring(
    basePathSelector(state).length + 1 + routeIdSelector(state).toString().length);

function mapStateToProps(state: ApplicationState): DetailProps {
    return {
        ohCase: ohCaseSelector(state),
        path: state.router.pathname,
        basePath: basePathSelector(state),
        tab: currentTab(state),
        currentCustomer: state.currentUser.currentCustomer,
        currentUser: state.currentUser
    };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
    return {
        push: (path: string) => dispatch(push(path, {})),
        loadDocuments: (ohCaseId: string) => dispatch(loadOhCaseDocuments({ ohCaseId }))
    };
}

export const Detail =  connect(mapStateToProps, mapDispatchToProps)(DetailUnconnected);
